import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { GlobalState } from 'app/global.state';
import { Menu } from 'app/shared/models/control/menu';
import { environment } from 'environments/environment';
import { normalizeAppUrl } from 'app/shared/utils/app-url.util';

/**
 * Single source of truth for "what happens when a PagesMenu node is activated".
 *
 * This unifies two behaviors that exist separately today:
 *  - baMainMenuItem.goto() (sidebar): lastChild+!url / url / expand-children, plus isCpoeManagement.
 *  - home.component.ts goto(): the same 3-way split plus 8 additional external-link flags
 *    (directReport, isAppointment, inNursingManagement, isOperationManagement, isCpoeManagement,
 *    isOncologyManagement, isCateringManagement, isVatIntegartionManagement, isHomeCare).
 *
 * The new shell's flyout/rail replace the sidebar as the primary way to reach a leaf screen, so it
 * must implement the full superset - otherwise external-link screens that only worked from the Home
 * grid before would silently stop working when reached from the flyout. `Home.goto()` itself is left
 * untouched; it keeps serving Home.html's own grid.
 */
@Injectable({ providedIn: 'root' })
export class MenuNavigationService {

    constructor(private readonly router: Router, private readonly state: GlobalState) { }

    /**
     * Returns true if `navigate()` will perform a router/external navigation for this item.
     * Returns false when the item has children and is not itself navigable - callers (e.g.
     * module-sections-tree) should toggle local expand/collapse state instead in that case.
     */
    isNavigable(item: Menu): boolean {
        return !!(item.url || (item.lastChild && !item.url) || this.isExternalLink(item));
    }

    /**
     * The app url this node opens as a screen, or null when activating it opens no screen at
     * all - branch nodes, and report viewers / external-system links, which open in a separate
     * browser tab rather than a desktop window. Mirrors navigate()'s branch structure so "is
     * this screen currently open?" can be answered without a second copy of that logic.
     */
    screenUrl(item: Menu): string | null {
        if (item.directReport || this.isExternalLink(item)) {
            return null;
        }
        if (item.lastChild && !item.url) {
            return normalizeAppUrl('/pages/home/' + item.id);
        }
        return item.url ? normalizeAppUrl(item.url) : null;
    }

    navigate(item: Menu): void {
        if (item.directReport) {
            this.openReport(item);
            return;
        }
        if (this.isExternalLink(item)) {
            this.openLinkInNewTab(this.externalUrl(item));
            return;
        }
        if (item.lastChild && !item.url) {
            localStorage.setItem('HomeItems', JSON.stringify(item));
            this.router.navigateByUrl('/pages/home/' + item.id);
            this.collapseMenu();
            return;
        }
        if (item.url) {
            this.router.navigateByUrl(item.url);
            this.collapseMenu();
        }
        // No url/lastChild/external/report: item is a pure branch node. Caller owns expand/collapse.
    }

    /** Same enableAppointment()/enableMobile() children-filtering as baMainMenuItem.goto(), ported verbatim. */
    filterChildren(children: Menu[] | null | undefined): Menu[] {
        let items = children || [];
        if (!environment.enableAppointment) {
            items = items.filter(f => !f.isAppointment);
        }
        if (!environment.mobileIntegrationEnabled) {
            items = items.filter(f => !f.isMobile);
        }
        return items;
    }

    isExternalLink(item: Menu): boolean {
        return !!(item.isAppointment || item.inNursingManagement || item.isOperationManagement
            || item.isCpoeManagement || item.isOncologyManagement || item.isCateringManagement
            || item.isVatIntegartionManagement || item.isHomeCare || item.isDynamicModule);
    }

    /** Builds the same external URL home.component.ts's getUrlOrExternalType(item, true) would. */
    externalUrl(item: Menu): string {
        if (item.isAppointment) {
            return environment.appointmentURI + item.url;
        }
        if (item.inNursingManagement) {
            return environment.nursingManagement + item.url;
        }
        if (item.isOperationManagement) {
            return environment.operationManagement + item.url;
        }
        if (item.isCpoeManagement) {
            return environment.cpoeManagement + item.url;
        }
        if (item.isOncologyManagement) {
            return environment.oncologyManagement + item.url;
        }
        if (item.isCateringManagement) {
            return environment.cateringManagement + item.url;
        }
        if (item.isVatIntegartionManagement) {
            return environment.VatIntegrationManagement + item.url;
        }
        if (item.isHomeCare) {
            return environment.homeCareUrl + item.url;
        }
        // Not from home.component.ts - the Tools rail module's shared dynamic modules never
        // appeared on Home's grid. Same viewer host the old sidebar's ba-dynamic-modules-menu
        // opened them at (BaseComponent.dynamicFormConstantUrl()).
        if (item.isDynamicModule) {
            return environment.dynamicFormViewer + item.url;
        }
        return item.url;
    }

    private openReport(item: Menu): void {
        const url = item.directReportWithParameter ? item.urlReport : environment.reportViewer + item.urlReport;
        this.openLinkInNewTab(url);
    }

    openLinkInNewTab(url: string): void {
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    private collapseMenu(): void {
        this.state.notifyDataChanged('menu.isCollapsed', true);
    }
}
